import { browser } from '$app/environment';
import { readable } from 'svelte/store';

// TYPES
type MediaQueriesState = {
	classNames: string;
	[query: string]: boolean | string;
};

type MediaQueryListObject = {
	[query: string]: MediaQueryList;
};

type Queries = typeof queries; // Define type for queries
type QueryName = keyof Queries; // Define a key type

export const columns = {
	narrow: 500,
	normal: 700,
	wide: 900,
	'full-width': 1920,
};

export const sizes = {
	small: 1,
	medium: 640,
	wide: 992,
	hd: 1280,
};

export const queries = {
	small: `(min-width: ${sizes.small}px)`,
	medium: `(min-width: ${sizes.medium}px)`,
	wide: `(min-width: ${sizes.wide}px)`,
	hd: `(min-width: ${sizes.hd}px)`,
	reducedMotion: '(prefers-reduced-motion: reduce)',
	desktop: '(hover: hover) and (pointer: fine)',
	short: '(max-width: 650px) and (max-height: 770px)',
	tall: '(min-width: 650px) and (min-height: 770px)',
};

function calculateMedia(mqls: MediaQueryListObject) {
	const media: MediaQueriesState = { classNames: '' };
	const mediaClasses: string[] = [];
	for (const name in mqls) {
		if (mqls[name as QueryName]) {
			// Ensure name is a valid index
			media[name as QueryName] = mqls[name as QueryName]?.matches ?? false;
			if (media[name as QueryName]) mediaClasses.push(`mq-${name}`);
		}
	}
	media.classNames = mediaClasses.join(' ');
	return media;
}

const mediaQueries = readable<MediaQueriesState>({ classNames: '' }, (set) => {
	if (!browser) return;
	const mqls: MediaQueryListObject = {};
	const onChange = () => set(calculateMedia(mqls));

	if (browser) {
		for (const q in queries) {
			mqls[q as QueryName] = window.matchMedia(queries[q as QueryName]);
			mqls[q as QueryName]?.addListener(onChange);
		}

		onChange();
	}

	return () => {
		for (const q in mqls) {
			mqls[q as QueryName]?.removeListener(onChange);
		}
	};
});

export default mediaQueries;
